/* Progressive 3D enhancement. CAD tooling and the renderer load separately. */
(() => {
  if (customElements.get('kyu-camera-viewer')) return;
  const modules = new Map();
  const getRenderer = (url) => {
    if (!modules.has(url)) {
      const pending = import(url).catch((error) => {
        if (modules.get(url) === pending) modules.delete(url);
        throw error;
      });
      modules.set(url, pending);
    }
    return modules.get(url);
  };

  class KyuCameraViewer extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.viewport = this.querySelector('[data-viewport]');
      this.poster = this.querySelector('[data-poster]');
      this.hint = this.querySelector('[data-hint]');
      this.status = this.querySelector('[data-status]');
      this.demoStatus = this.querySelector('[data-demo-status]');
      this.buttons = Object.fromEntries([...this.querySelectorAll('[data-action]')].map((el) => [el.dataset.action, el]));
      this.touchPointers = new Set();
      this.touchActive = false;
      this.detached = false;
      this.inView = false;
      this.suspended = false;
      this.presentationStarted = false;
      this.drag = null;
      this.hold = null;
      this.demoGesture = null;
      this.classList.remove('is-dragging', 'is-holding');
      this.setAttribute('data-state', 'idle');
      this.removeAttribute('data-touch-active');
      if (!this.viewport) return;
      this.resetDetached();
      this.buttons.load.textContent = this.dataset.load;
      this.buttons.load.disabled = false;
      if (this.buttons.operate) this.buttons.operate.hidden = true;
      this.buttons.done.hidden = true;
      this.buttons.detach.disabled = true;
      if (this.buttons.reset) this.buttons.reset.disabled = true;
      this.setDemoAvailable(false);
      this.announce('');
      const on = (element, type, callback, options = {}) => element.addEventListener(type, callback, { ...options, signal: this.events.signal });
      on(this, 'click', (event) => this.onAction(event));
      on(this.viewport, 'pointerdown', (event) => this.pointerDown(event));
      on(this.viewport, 'pointermove', (event) => this.pointerMove(event));
      on(this.viewport, 'pointerup', (event) => this.pointerEnd(event));
      on(this.viewport, 'pointercancel', (event) => this.pointerEnd(event, true));
      on(this.viewport, 'lostpointercapture', (event) => {
        if (this.hold?.id === event.pointerId) this.cancelHold();
        if (this.drag?.id === event.pointerId) this.stopDrag();
        if (this.demoGesture?.id === event.pointerId) this.cancelDemoGesture();
      });
      // Also clean up pointers released outside the viewport or after capture loss.
      on(window, 'pointerup', (event) => this.pointerEnd(event), { passive: true });
      on(window, 'pointercancel', (event) => this.pointerEnd(event, true), { passive: true });
      on(this.viewport, 'pointerleave', () => this.cancelHold());
      on(this.viewport, 'contextmenu', (event) => { if (this.hold || this.touchActive) event.preventDefault(); });
      on(this.viewport, 'dragstart', (event) => event.preventDefault());
      on(this.viewport, 'keydown', (event) => this.keyDown(event));
      on(document, 'keydown', (event) => { if (event.key === 'Escape' && this.touchActive) this.exitMode(); });
      on(document, 'visibilitychange', () => {
        if (document.hidden) this.exitMode();
        this.updateVisibility();
      });
      on(window, 'blur', () => this.exitMode());
      on(window, 'pagehide', () => {
        this.suspended = true;
        this.exitMode();
        this.updateVisibility();
      });
      on(window, 'pageshow', () => {
        this.suspended = false;
        this.updateVisibility();
      });
      on(this, 'focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.exitMode();
      });

      const connection = navigator.connection;
      this.manualLoad = Boolean(connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType || ''));
      this.buttons.load.hidden = false;
      this.setHint(this.defaultHint());
      if ('IntersectionObserver' in window) {
        this.nearObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.manualLoad) {
            this.nearObserver.disconnect();
            this.load();
          }
        }, { rootMargin: '400px 0px' });
        this.nearObserver.observe(this);
        this.visibleObserver = new IntersectionObserver((entries) => {
          this.inView = entries.some((entry) => entry.isIntersecting);
          if (!this.inView) this.exitMode();
          this.updateVisibility();
        });
        this.visibleObserver.observe(this.viewport);
      } else {
        // Without visibility APIs, the explicit button still works.
        this.inView = true;
      }
    }

    disconnectedCallback() {
      this.exitMode();
      this.stopPresentationWatch();
      this.events?.abort();
      this.events = null;
      this.cancelLoad();
      this.nearObserver?.disconnect();
      this.visibleObserver?.disconnect();
      this.viewer?.dispose();
      this.viewer = null;
      if (this.poster) this.poster.hidden = false;
    }

    defaultHint() {
      return matchMedia('(pointer: coarse)').matches ? this.dataset.readyTouch : this.dataset.readyDesktop;
    }
    setHint(text) { if (this.hint) this.hint.textContent = text || ''; }
    announce(text) { if (this.status) this.status.textContent = text || ''; }
    updateVisibility() {
      this.viewer?.setVisible(this.inView && !document.hidden && !this.suspended);
      this.tryStartPresentation();
    }
    stopPresentationWatch() {
      this.presentationEvents?.abort();
      this.presentationEvents = null;
    }
    watchPresentation() {
      this.stopPresentationWatch();
      if (!this.viewer || this.presentationStarted) return;
      // IntersectionObserver controls rendering visibility. While startup is
      // pending, scrolling also catches tall viewports crossing the center.
      this.presentationEvents = new AbortController();
      const options = { passive: true, signal: this.presentationEvents.signal };
      window.addEventListener('scroll', () => this.tryStartPresentation(), options);
      window.addEventListener('resize', () => this.tryStartPresentation(), options);
    }
    tryStartPresentation() {
      if (!this.isConnected || !this.viewer || this.presentationStarted || document.hidden || this.suspended) return;
      const rect = this.viewport.getBoundingClientRect();
      const width = window.innerWidth;
      const height = window.innerHeight;
      if (!(rect.width > 0 && rect.height > 0 && width > 0 && height > 0)) return;
      const visibleWidth = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
      if (!visibleWidth || !visibleHeight) return;
      const fullyVisible = visibleWidth >= rect.width * 0.98 && visibleHeight >= rect.height * 0.98;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const centered = centerX >= 0 && centerX <= width && centerY >= height * 0.4 && centerY <= height * 0.6;
      if (!fullyVisible && !centered) return;
      const viewer = this.viewer;
      this.presentationStarted = true;
      this.stopPresentationWatch();
      // The measured rectangle can be current before the observer callback.
      this.inView = true;
      viewer.setVisible(true);
      if (this.viewer !== viewer) return;
      const showBoot = !this.startupPlayed;
      this.startupPlayed = true;
      viewer.startPresentation?.({ showBoot });
    }
    resetDetached() {
      this.detached = false;
      this.buttons.detach.setAttribute('aria-pressed', 'false');
      this.buttons.detach.textContent = this.dataset.detach;
    }
    setDemoAvailable(available) {
      for (const action of ['record', 'switch-camera']) {
        if (this.buttons[action]) this.buttons[action].disabled = !available;
      }
      if (!available) {
        this.demoState = null;
        this.removeAttribute('data-demo-state');
        this.removeAttribute('data-camera');
        this.removeAttribute('data-startup-phase');
        this.buttons.record?.setAttribute('aria-pressed', 'false');
        if (this.buttons.record && this.dataset.record) this.buttons.record.textContent = this.dataset.record;
        if (this.demoStatus) this.demoStatus.textContent = '';
      }
    }
    updateDemoState(state) {
      if (!state) return;
      this.demoState = state;
      this.setAttribute('data-demo-state', state.mode);
      this.setAttribute('data-camera', state.camera);
      if (state.startupPhase) this.setAttribute('data-startup-phase', state.startupPhase);
      else this.removeAttribute('data-startup-phase');
      const recording = state.mode === 'recording';
      const detached = state.detached || state.mode === 'detached';
      const transition = state.transition || state.mode === 'saving' || state.mode === 'attaching';
      if (this.buttons.record) {
        this.buttons.record.setAttribute('aria-pressed', String(recording));
        const label = recording ? this.dataset.stop : this.dataset.record;
        if (label) this.buttons.record.textContent = label;
        // Firmware lets an active recording finish after memory removal, and
        // accepts the full-memory button press to display its Done notice.
        this.buttons.record.disabled = Boolean(!recording && (detached || transition));
      }
      if (this.buttons['switch-camera']) this.buttons['switch-camera'].disabled = Boolean(recording || detached || transition);
      const label = {
        idle: this.dataset.demoReady, recording: this.dataset.demoRecording,
        saving: this.dataset.demoSaving, detached: this.dataset.demoDetached,
        attaching: this.dataset.demoAttached,
      }[state.mode];
      const camera = state.camera === 'in' ? this.dataset.demoIn : this.dataset.demoOut;
      // Timers may update the display frequently; the live region only changes
      // for a new operating mode or camera, avoiding repeated announcements.
      const message = [label, camera].filter(Boolean).join(' · ');
      if (this.demoStatus && this.demoStatus.textContent !== message) this.demoStatus.textContent = message;
    }
    cancelLoad() {
      const abort = this.loadAbort;
      this.loadAbort = null;
      this.loading = null;
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
      abort?.abort();
    }

    async load() {
      if (this.viewer || this.loading) return this.loading;
      this.setAttribute('data-state', 'loading');
      this.buttons.load.disabled = true;
      this.announce(this.dataset.loading);
      this.setHint(this.dataset.loading);
      const abort = new AbortController();
      this.loadAbort = abort;
      this.loadTimeout = setTimeout(() => {
        if (this.loadAbort === abort) this.fail();
      }, 15000);
      this.loading = (async () => {
        let createdViewer = null;
        let initialDemoState = null;
        try {
          const rendererUrl = new URL(this.dataset.rendererUrl, document.baseURI);
          if (this.rendererRetry) rendererUrl.searchParams.set('kyu_retry', String(this.rendererRetry));
          this.rendererRequestUrl = rendererUrl.href;
          const module = await getRenderer(this.rendererRequestUrl);
          if (abort.signal.aborted || !this.isConnected || this.loadAbort !== abort) return;
          createdViewer = await module.createViewer({
            host: this.viewport,
            deferStartup: true,
            modelUrl: new URL(this.dataset.modelUrl, document.baseURI).href,
            fontUrl: this.dataset.fontUrl ? new URL(this.dataset.fontUrl, document.baseURI).href : undefined,
            signal: abort.signal,
            labels: {
              noMemory: this.dataset.screenNoMemory, no: this.dataset.screenNo,
              me: this.dataset.screenMe, you: this.dataset.screenYou, limit: this.dataset.screenLimit, done: this.dataset.screenDone,
            },
            onStateChange: (state) => {
              if (abort.signal.aborted || !this.isConnected) return;
              if (createdViewer && this.viewer === createdViewer) this.updateDemoState(state);
              else if (this.loadAbort === abort) initialDemoState = state;
            },
            onContextLost: () => {
              if (this.loadAbort === abort || (createdViewer && this.viewer === createdViewer)) this.fail();
            },
          });
          if (abort.signal.aborted || !this.isConnected || this.loadAbort !== abort) { createdViewer.dispose(); return; }
          this.viewer = createdViewer;
          this.presentationStarted = false;
          this.resetDetached();
          this.setAttribute('data-state', 'ready');
          if (this.poster) this.poster.hidden = true;
          this.buttons.load.hidden = true;
          if (this.buttons.operate) this.buttons.operate.hidden = false;
          this.buttons.detach.disabled = false;
          if (this.buttons.reset) this.buttons.reset.disabled = false;
          this.setDemoAvailable(true);
          this.updateDemoState(createdViewer.getState?.() || initialDemoState);
          this.setHint(this.defaultHint());
          this.announce('');
          this.watchPresentation();
          this.updateVisibility();
        } catch (error) {
          if (this.isConnected && this.loadAbort === abort) this.fail(error.code === 'WEBGL_UNAVAILABLE');
        } finally {
          if (this.loadAbort === abort) {
            clearTimeout(this.loadTimeout);
            this.loadTimeout = null;
            this.loadAbort = null;
            this.loading = null;
            this.buttons.load.disabled = false;
          }
        }
      })();
      return this.loading;
    }

    fail(unsupported = false) {
      // Native import promises cannot be aborted. A new URL also bypasses the
      // browser's cached failed or stalled module request on the next retry.
      if (this.rendererRequestUrl) modules.delete(this.rendererRequestUrl);
      this.rendererRetry = (this.rendererRetry || 0) + 1;
      this.cancelLoad();
      this.exitMode();
      this.stopPresentationWatch();
      this.viewer?.dispose();
      this.viewer = null;
      this.presentationStarted = false;
      this.resetDetached();
      this.setAttribute('data-state', 'error');
      if (this.poster) this.poster.hidden = false;
      this.buttons.load.hidden = false;
      this.buttons.load.disabled = false;
      this.buttons.load.textContent = this.dataset.retry;
      if (this.buttons.operate) this.buttons.operate.hidden = true;
      this.buttons.detach.disabled = true;
      if (this.buttons.reset) this.buttons.reset.disabled = true;
      this.setDemoAvailable(false);
      const message = unsupported ? this.dataset.unsupported : this.dataset.error;
      this.setHint(message);
      this.announce(message);
    }

    onAction(event) {
      const button = event.target.closest('[data-action]');
      if (!button || !this.contains(button) || button.disabled) return;
      switch (button.dataset.action) {
        case 'load': this.load(); break;
        case 'operate': if (this.viewer) this.enterMode(); break;
        case 'record':
          this.viewer?.showScreen?.();
          this.viewer?.record?.();
          break;
        case 'switch-camera':
          this.viewer?.showScreen?.();
          this.viewer?.switchCamera?.('left');
          break;
        case 'done':
          this.exitMode();
          (this.buttons.operate || this.viewport).focus({ preventScroll: true });
          break;
        case 'detach':
          if (!this.viewer) break;
          this.detached = !this.detached;
          this.viewer.detach(this.detached);
          button.setAttribute('aria-pressed', String(this.detached));
          button.textContent = this.detached ? this.dataset.attach : this.dataset.detach;
          this.announce(this.detached ? this.dataset.detached : this.dataset.attached);
          break;
        case 'reset': this.resetView(); break;
      }
    }

    resetView() {
      if (!this.viewer) return;
      this.viewer.reset();
      this.resetDetached();
      this.announce(this.dataset.attached);
    }

    enterMode() {
      if (!this.viewer) return;
      this.touchActive = true;
      this.setAttribute('data-touch-active', 'true');
      if (this.buttons.operate) this.buttons.operate.hidden = true;
      this.buttons.done.hidden = false;
      this.setHint(this.dataset.active);
      this.viewport.focus({ preventScroll: true });
    }
    exitMode() {
      this.cancelHold();
      this.stopDrag();
      this.cancelDemoGesture();
      this.touchPointers?.clear();
      this.touchActive = false;
      this.removeAttribute('data-touch-active');
      if (this.buttons) {
        this.buttons.done.hidden = true;
        if (this.buttons.operate) this.buttons.operate.hidden = !this.viewer;
      }
      if (this.viewer) this.setHint(this.defaultHint());
    }
    cancelHold() {
      clearTimeout(this.holdTimer);
      this.hold = null;
      this.classList.remove('is-holding');
      if (this.viewer && !this.touchActive) this.setHint(this.defaultHint());
    }
    stopDrag() {
      const drag = this.drag;
      this.drag = null;
      this.classList.remove('is-dragging');
      if (drag && this.viewport.hasPointerCapture(drag.id)) this.viewport.releasePointerCapture(drag.id);
    }
    cancelDemoGesture() {
      const gesture = this.demoGesture;
      this.demoGesture = null;
      if (gesture && this.viewport.hasPointerCapture(gesture.id)) this.viewport.releasePointerCapture(gesture.id);
    }
    startDrag(event, origin = event) {
      this.drag = { id: event.pointerId, x: origin.clientX, y: origin.clientY };
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.focus({ preventScroll: true });
      this.classList.add('is-dragging');
      event.preventDefault();
    }
    pointerDown(event) {
      if (!this.viewer || event.button !== 0) return;
      const touch = event.pointerType === 'touch';
      if (touch) {
        this.touchPointers.add(event.pointerId);
        if (this.touchPointers.size > 1) {
          this.cancelHold(); this.stopDrag(); this.cancelDemoGesture(); return;
        }
      }
      const target = this.viewer.hitTest?.(event.clientX, event.clientY);
      if (target === 'record' || target === 'screen') {
        this.cancelHold();
        this.demoGesture = {
          id: event.pointerId, target, x: event.clientX, y: event.clientY,
          touch, axis: null, moved: false,
        };
        this.viewport.setPointerCapture(event.pointerId);
        // Keep the browser's vertical scroll and pinch gestures available.
        // touch-action: pan-y is already set before this pointer begins.
        if (!touch) {
          this.viewport.focus({ preventScroll: true });
          event.preventDefault();
        }
        return;
      }
      if (touch && !this.touchActive) {
        this.cancelHold();
        this.hold = { id: event.pointerId, x: event.clientX, y: event.clientY, armed: false };
        this.holdTimer = setTimeout(() => {
          if (!this.hold) return;
          this.hold.armed = true;
          this.classList.add('is-holding');
          this.setHint(this.dataset.holdRelease);
        }, 450);
        return;
      }
      this.startDrag(event);
    }
    pointerMove(event) {
      const gesture = this.demoGesture;
      if (gesture?.id === event.pointerId) {
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.hypot(dx, dy) < 8 && !gesture.moved) return;
        gesture.moved = true;
        if (gesture.target === 'screen' && !gesture.axis) {
          // Wait for a clear direction before committing either screen swipe
          // or model movement; diagonal motion does not accidentally switch.
          if (Math.abs(dx) >= Math.abs(dy) * 1.35) gesture.axis = 'horizontal';
          else if (Math.abs(dy) >= Math.abs(dx) * 1.35) gesture.axis = 'vertical';
          else return;
        }
        if (gesture.target === 'screen' && gesture.axis === 'horizontal') {
          event.preventDefault();
          return;
        }
        this.cancelDemoGesture();
        if (gesture.touch && !this.touchActive) return;
        this.startDrag(event, { clientX: gesture.x, clientY: gesture.y });
      }
      if (this.hold && this.hold.id === event.pointerId) {
        if (Math.hypot(event.clientX - this.hold.x, event.clientY - this.hold.y) >= 8) this.cancelHold();
        return;
      }
      if (!this.viewer || !this.drag || this.drag.id !== event.pointerId) return;
      // Relative to the viewport, consistent across screen sizes.
      const scale = Math.PI * 2 / Math.max(this.viewport.clientWidth, 240);
      this.viewer.rotate((event.clientX - this.drag.x) * scale, (event.clientY - this.drag.y) * scale);
      this.drag.x = event.clientX;
      this.drag.y = event.clientY;
    }
    pointerEnd(event, cancelled = false) {
      this.touchPointers.delete(event.pointerId);
      const gesture = this.demoGesture?.id === event.pointerId ? this.demoGesture : null;
      if (gesture) {
        this.cancelDemoGesture();
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (!cancelled && this.viewer) {
          if (gesture.target === 'record' && !gesture.moved && Math.hypot(dx, dy) < 8 &&
              this.viewer.hitTest?.(event.clientX, event.clientY) === 'record') this.viewer.record?.();
          else if (gesture.target === 'screen' && gesture.axis === 'horizontal' &&
              Math.abs(dx) >= 24 && Math.abs(dx) >= Math.abs(dy) * 1.35) {
            this.viewer.switchCamera?.(dx < 0 ? 'left' : 'right');
          }
        }
      }
      const activate = !cancelled && this.hold?.id === event.pointerId && this.hold.armed;
      if (this.hold?.id === event.pointerId) this.cancelHold();
      if (this.drag?.id === event.pointerId) this.stopDrag();
      // touch-action changes only after this pointer ends, for the NEXT gesture.
      if (activate) this.enterMode();
    }
    keyDown(event) {
      if (!this.viewer) return;
      if (event.key === 'Escape') { this.exitMode(); return; }
      const step = Math.PI / 18;
      const keys = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (keys[event.key] && this.touchActive) { event.preventDefault(); this.viewer.rotate(...keys[event.key]); }
      else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.enterMode(); }
      else if (event.key === 'Home') { event.preventDefault(); this.resetView(); }
    }
  }
  customElements.define('kyu-camera-viewer', KyuCameraViewer);
})();
